import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "@remix-run/react";
import { auth } from "../utils/auth/firebase-service"; // Adjust the import to your Firebase config
import { onAuthStateChanged, User } from "firebase/auth";
import { getProfile } from "~/utils/ServerRequest";

// Add this at the top of the file to declare window._paq
declare global {
    interface Window {
        _paq: any[];
    }
}

interface MatomoProviderProps {
    // Optional userTier can be passed when available.
    children: React.ReactNode;
}

const MatomoProvider: React.FC<MatomoProviderProps> = ({ children }) => {
    const location = useLocation();
    const [userToken, setUserToken] = useState<string | null>(null);
    const [userProfile, setUserProfile] = useState<any>(null);
    const matomoInitialized = useRef(false);

    // Listen for changes in the auth state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    setUserToken(user.uid);
                    // If Matomo has already been initialized, update the userID on the fly.
                    const profile = await getProfile()
                    setUserProfile(profile);

                    if (window._paq) {
                        window._paq.push(["setUserId", user.uid]);
                    }
                } catch (error) {
                    console.error("Failed to get user token:", error);
                    setUserToken(null);
                }
            } else {
                // User is signed out.
                setUserToken(null);
                if (window._paq) {
                    // Optionally clear the user ID.
                    window._paq.push(["setUserId", ""]);
                }
            }
        });

        // Clean up the listener on unmount.
        return () => unsubscribe();
    }, []);

    // Initialize Matomo tracking.
    useEffect(() => {
        const _paq = (window._paq = window._paq || []);
        // If the user is logged in, set the user ID from the token.
        if (userToken) {
            _paq.push(["setUserId", userToken]);
        }
        // Optionally, set a custom dimension for the user's subscription tier if available.
        if (userProfile && userProfile.subscription) {
            _paq.push(["setCustomDimension", 1, userProfile.subscription]);
        }
        // Track the initial page view and enable link tracking.
        _paq.push(["setCustomUrl", location.pathname]);
        _paq.push(["setDocumentTitle", document.title]);
        _paq.push(["trackPageView"]);
        _paq.push(["enableLinkTracking"]);
        // Only insert the Matomo script and setTrackerUrl/setSiteId once
        if (!matomoInitialized.current) {
            (function () {
                const u = "https://analytics.blueskeye.com/"; // Replace with your Matomo URL.
                _paq.push(["setTrackerUrl", u + "matomo.php"]);
                _paq.push(["setSiteId", "5"]); // Replace with your site ID.
                const d = document,
                    g = d.createElement("script"),
                    s = d.getElementsByTagName("script")[0];
                g.type = "text/javascript";
                g.async = true;
                g.defer = true;
                g.src = u + "matomo.js";
                s.parentNode?.insertBefore(g, s);
            })();
            matomoInitialized.current = true;
        }
        // We only run this initialization once for script and tracker config.
    }, [userToken, userProfile, location.pathname]);

    // Track a page view on route changes.
    useEffect(() => {
        if (window._paq) {
            window._paq.push(["setCustomUrl", location.pathname]);
            window._paq.push(["setDocumentTitle", document.title]);
            window._paq.push(["trackPageView"]);
        }
    }, [location.pathname]);

    return <>{children}</>;
};

export default MatomoProvider;
